import { Component, OnDestroy, OnInit } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { MatSnackBar, MatSnackBarConfig } from "@angular/material/snack-bar";
import { MatTableDataSource } from "@angular/material/table";
import { Router } from "@angular/router";
import { NotificationsService } from "angular2-notifications";
import { AlertToasterOptions } from "app/class/alert-toaster-options";
import { HistoryTypes } from "app/enums/ticket-history-type.enum";
import { TicketServices } from "app/enums/ticket-services.enum";
import { TicketStatuses } from "app/enums/TicketStatuses.enum";
import { ITicket } from "app/interfaces/i-ticket";
import { ITicketHistory } from "app/interfaces/i-ticket-history";
import { ITicketNew } from "app/interfaces/i-ticket-new";
import {
    ITicketScheduled,
    TicketScheduleRequest,
} from "app/interfaces/i-ticket-scheduled";
import { IUser } from "app/interfaces/i-user";
import { GoogleAnalyticsService } from "app/services/analytics/google-analitics-service";
import { ApiCalendarService } from "app/services/api/api-calendar-service";
import { ApiLoginService } from "app/services/api/api-login.service";
import { ApiQueueService } from "app/services/api/api-queue.service";
import { ApiTicketHistoryService } from "app/services/api/api-ticket-history.service";
import { ApiTicketServiceService } from "app/services/api/api-ticket-service-service";
import { ApiTicketService } from "app/services/api/api-ticket.service";
import { ApiUserService } from "app/services/api/api-user.service";
import { AuthService } from "app/services/auth/auth.service";
import { LocalStorageService } from "app/services/local-storage/local-storage.service";
import { SocketService } from "app/services/socket/socket.service";
import { WsEvents } from "app/type/ws-events";
import { environment } from "environments/environment";
import * as _ from "lodash";
import { keyBy } from "lodash";
import moment from "moment";
import { from, merge, Observable, Subject } from "rxjs";
import {
    filter,
    finalize,
    map,
    mergeMap,
    takeUntil,
    tap,
} from "rxjs/operators";
import { TicketCalendarEventType } from "../../enums/ticket-calendar-event-type.enum";
import { ErrorMessageTranslatorService } from "./../../ErrorMessageTranslatorService";
import { DialogEditPrivacyPolicyComponent } from "./../../layout/components/navbar/dialog-component/privacy/edit-privacy.component";
import { DialogProfileComponent } from "./../../layout/components/navbar/dialog-component/profile/profile.component";
import { ApiPlatformService } from "./../../services/api/api-platform-service.service";
import { DialogForgotPassword } from "./dialog-component/forgot-password/dialog-forgot-password.component";
import { DialogLogin } from "./dialog-component/login/dialog-login.component";
import { DialogNewTicket } from "./dialog-component/new-ticket/dialog-new-ticket.component";
import { DialogRegistrationComponent } from "./dialog-component/registration/registration.component";

@Component({
    standalone: false,
    selector: "home",
    templateUrl: "./home.component.html",
    styleUrls: ["./home.component.scss"],
})
export class HomeComponent implements OnInit, OnDestroy {
    public ticketServices;
    public activeServices;
    public options = AlertToasterOptions;
    public userLogged: boolean;
    public askUserDataConfirmation: boolean;
    public welcomePhrase: string = "";
    public hasScheduledService: boolean;
    private dataModal: any;
    private user: IUser;
    dataSource = new MatTableDataSource();
    statusesToSearch: number[] = [
        TicketStatuses.SCHEDULED_UNASSIGNED,
        TicketStatuses.SCHEDULED_ASSIGNED,
        TicketStatuses.ONLINE,
        TicketStatuses.REFUSED,
    ];
    /**
     * Impedisce che un doppio click rapido sul tasto "Procedi"
     * apra due richieste di servizio in contemporanea.
     */
    public serviceRequestInProgress = false;
    private destroy$ = new Subject<void>();

    constructor(
        private router: Router,
        public dialog: MatDialog,
        private apiCalendarService: ApiCalendarService,
        private apiTicketServiceService: ApiTicketServiceService,
        private apiLoginService: ApiLoginService,
        private authService: AuthService,
        private apiTickeService: ApiTicketService,
        private apiTicketHistoryService: ApiTicketHistoryService,
        private storage: LocalStorageService,
        private toast: NotificationsService,
        private socketService: SocketService,
        private apiUserService: ApiUserService,
        private apiQueueService: ApiQueueService,
        private platformService: ApiPlatformService,
        public googleAnalyticsService: GoogleAnalyticsService,
        public snackBar: MatSnackBar,
        private errorMessageTranslatorService: ErrorMessageTranslatorService,
    ) {
        if (!this.storage.getItem("user")) {
            this.router.navigate(["/login"]);
        }
        this.welcomePhrase = environment.welcomePhrase;
        this.user = this.storage.getItem("user");

        const navigation = this.router.getCurrentNavigation();
        if (navigation?.extras.state) {
            const state = navigation.extras.state as {
                requirePrivacy: boolean;
            };
            if (state.requirePrivacy) {
                this.dialog
                    .open(DialogEditPrivacyPolicyComponent, {
                        hasBackdrop: true,
                        data: {
                            modalData: this.user.userdata,
                        },
                    })
                    .afterClosed()
                    .pipe(
                        filter((result) => !!result),
                        mergeMap((result) => {
                            this.user.userdata = result;
                            return this.apiUserService.apiChangeProfile(
                                this.user,
                            );
                        }),
                    )
                    .subscribe({
                        next: (user) => {
                            this.storage.setItem("user", user);
                            this.toast.success(
                                "Aggiornamento Privacy",
                                "Configurazione privacy aggiornata con successo",
                            );
                        },
                        error: (err) => {
                            const errorMessage = _.get(
                                err,
                                "error.message",
                                "",
                            );
                            const errorMessageTranslated =
                                this.errorMessageTranslatorService.translate(
                                    errorMessage,
                                );
                            this.toast.error(
                                "Aggiornamento Privacy",
                                errorMessageTranslated,
                            );
                        },
                    });
            }
        }
    }

    ngOnInit(): void {
        this.storage.getChangesOf<any>("data").subscribe((data) => {
            this.user = data.user;
        });
        if (!this.storage.getItem("browser-info")) {
            this.openBrowserInfo();
        }
        this.askUserDataConfirmation =
            !this.user.userdata.privacyaccept || !this.user.userdata.id_region;
        this.googleAnalyticsService.pageEmitter("HomePage");
        this.platformService
            .getOneById(environment.platform_id)
            .pipe(
                mergeMap((platform) =>
                    this.apiTicketServiceService.getAll(platform.id_tenant),
                ),
                tap((services) => {
                    this.hasScheduledService = services.some(
                        (service) => service.type === "scheduled",
                    );
                    const visibleServices = _.filter(services, {
                        isVisible: true,
                    });
                    this.ticketServices = keyBy(
                        visibleServices,
                        (serviceItem) => serviceItem.service,
                    );

                    if (this.hasScheduledService) {
                        this.getTickets();
                    }
                }),
                mergeMap((services) => from(services)),
                mergeMap((service: any) =>
                    this.apiCalendarService.apiIsActive(service.id).pipe(
                        map((status) => ({
                            description: service.description,
                            service: service.service,
                            status: status,
                        })),
                    ),
                ),
            )
            .subscribe((data) => {
                if (!_.isEmpty(this.ticketServices)) {
                    this.ticketServices[data.service].isOpen = data.status;
                    this.activeServices = Object.values(this.ticketServices);
                }
            });
        this.getTicketsWebsocket();

        this.authService.change().subscribe((data) => {
            if (!!data) {
                this.userLogged = true;
            } else {
                this.userLogged = false;
                this.router.navigate(["login"]);
            }
        });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    public isOpen(service: string): boolean {
        return this.ticketServices
            ? this.ticketServices[service].isOpen
            : false;
    }

    public clickService(service: any): void {
        if (this.serviceRequestInProgress) {
            return;
        }

        this.googleAnalyticsService.eventEmitter("ClickCard", service.service);
        if (service.type !== "scheduled" && !this.isOpen(service.service)) {
            return;
        }

        this.serviceRequestInProgress = true;

        this.apiLoginService.apiGetToken().subscribe({
            next: (data) => {
                if (service.type !== "scheduled") {
                    if (environment.openTicketWithoutModal) {
                        this.createTicketAndFirstHistory(
                            {
                                category: environment.defaultTicketCategory,
                                phone: "",
                                description: "",
                            },
                            service.service,
                        )
                            .pipe(finalize(() => this.releaseServiceRequest()))
                            .subscribe({
                                next: (ticketHistory) => {
                                    this.router.navigate([
                                        "/waiting",
                                        ticketHistory.id_ticket,
                                    ]);
                                },
                                error: (err) => {
                                    console.error(err);
                                    this.toast.error(
                                        "Nuovo Ticket",
                                        "Aspetta un minuto prima di chiedere un'altra richiesta",
                                    );
                                },
                            });
                    } else {
                        this.openNewTicketModal(service);
                    }
                } else {
                    this.openNewTicketModal(service);
                }
            },
            error: (error) => {
                this.openLoginModal(service);
            },
        });
    }

    /**
     * Sblocca il tasto "Procedi" una volta concluso (o annullato) il flusso
     * di richiesta del servizio.
     */
    private releaseServiceRequest(): void {
        this.serviceRequestInProgress = false;
    }

    private openLoginModal(service: any): void {
        this.dialog
            .open(DialogLogin)
            .afterClosed()
            .subscribe((data) => {
                if (data) {
                    if (data.registration) {
                        this.releaseServiceRequest();
                        this.dialog.open(DialogRegistrationComponent);
                    } else if (data.forgot) {
                        this.dialog
                            .open(DialogForgotPassword)
                            .afterClosed()
                            .subscribe((fromForgot) => {
                                if (!!fromForgot.goLogin) {
                                    this.openLoginModal(service.service);
                                } else {
                                    this.releaseServiceRequest();
                                }
                            });
                    } else {
                        this.socketService.sendMessage("welcome-message", {
                            userToken:
                                this.authService.getToken().token_session,
                        });
                        this.openNewTicketModal(service);
                    }
                } else {
                    this.releaseServiceRequest();
                }
            });
    }

    private openNewTicketModal(service: any): void {
        this.serviceRequestInProgress = true;
        const isScheduled = service.type === "scheduled";
        const serviceType = TicketServices[
            service.service
        ] as unknown as number; //TODO: to be removed

        this.apiQueueService
            .apiGetActiveOperator(serviceType)
            .pipe(
                tap((data) => {
                    if (data.operatorActive < 1 && !isScheduled) {
                        this.toast.error(
                            "Richiesta Nuovo Ticket",
                            "Non ci sono operatori attivi in questo momento. Riprova più tardi!",
                        );
                    }
                }),
                filter((data) => data.operatorActive > 0 || isScheduled),
                filter(
                    (data) =>
                        serviceType === TicketServices.CHAT ||
                        serviceType === TicketServices.VIDEOCHAT ||
                        serviceType === TicketServices.VIDEOCHAT_SCHEDULED ||
                        serviceType ===
                            TicketServices.VIDEOCHAT_MEDICI_SCHEDULED ||
                        serviceType === TicketServices.ECUTE_WEB_WIDGET,
                ),
                mergeMap(() =>
                    this.dialog
                        .open(DialogNewTicket, {
                            hasBackdrop: true,
                            data: {
                                service: service.service,
                                title: this.ticketServices[service.service]
                                    .title,
                                icon: this.ticketServices[service.service]
                                    .webIcon,
                                color: this.ticketServices[service.service]
                                    .color,
                                type: this.ticketServices[service.service].type,
                            },
                        })
                        .afterClosed(),
                ),
                filter((dataFromModal) => !!dataFromModal),
                mergeMap((dataFromModal) =>
                    !isScheduled
                        ? this.createTicketAndFirstHistory(
                              dataFromModal,
                              service.service,
                          )
                        : this.createTicket(dataFromModal, service.service),
                ),
                finalize(() => this.releaseServiceRequest()),
            )
            .subscribe({
                next: (ticketHistory: ITicketHistory) => {
                    if (
                        serviceType === TicketServices.VIDEOCHAT_SCHEDULED ||
                        serviceType ===
                            TicketServices.VIDEOCHAT_MEDICI_SCHEDULED
                    ) {
                        this.toast.success(
                            "Nuova Richiesta Programmata",
                            "Ti sarà inviata una notifica quando la tua richiesta verrà presa in carico",
                        );
                    } else {
                        this.router.navigate([
                            "/waiting",
                            ticketHistory.id_ticket,
                        ]);
                    }
                },
                error: (err) => {
                    console.error(err);
                    this.toast.error(
                        "Nuovo Ticket",
                        "Aspetta un minuto prima di chiedere un'altra richiesta",
                    );
                },
            });
    }

    private createTicketAndFirstHistory(
        dataModal,
        service: string,
    ): Observable<ITicketHistory> {
        return this.apiTickeService
            .create({
                id_service: TicketServices[service],
                id_category: dataModal.category,
                phone: dataModal.phone,
                id_platform: environment.platform_id,
            })
            .pipe(
                mergeMap((fromNewTicket: ITicket) =>
                    this.apiTicketHistoryService.create({
                        id_ticket: fromNewTicket.id,
                        id_type: HistoryTypes.INITIAL,
                        action: dataModal.description,
                    }),
                ),
            );
    }

    private createTicket(
        dataModal: Partial<ITicketNew> & {
            date?: Date;
            schedule_hours: string;
        },
        service: string,
    ): Observable<ITicket> {
        const user = this.user.userdata.name + " " + this.user.userdata.surname;
        const time_start = !dataModal.date
            ? moment(dataModal.schedule_hours, "HH:mm").toDate()
            : moment(dataModal.date)
                  .set({
                      hours: parseInt(
                          dataModal.schedule_hours.split(":")[0],
                          10,
                      ),
                      minutes: parseInt(
                          dataModal.schedule_hours.split(":")[1],
                          10,
                      ),
                  })
                  .toDate();
        const end_to = moment(time_start)
            .add(dataModal.duration, "minutes")
            .toDate();
        const title =
            "TICKET-SCHEDULATED di " +
            user +
            " del: " +
            moment(time_start).format("DD/MM/YYYY - HH:mm");

        return this.apiTickeService.create(
            Object.assign({
                id_service: TicketServices[service],
                id_category: dataModal.id_category,
                id_platform: environment.platform_id,
                phone: dataModal.phone,
                calendarEvent: {
                    id: dataModal.schedulationDetail.id,
                    id_ticket: dataModal.schedulationDetail.id_ticket,
                    id_calendar_event_type:
                        TicketCalendarEventType.TICKET_SCHEDULATED,
                    is_remote: dataModal.schedulationDetail?.is_remote,
                    title: title,
                    address: dataModal.schedulationDetail.address,
                    city: dataModal.schedulationDetail.city,
                    in_place_reason: dataModal.schedulationDetail.reason,
                    time_start: time_start,
                    duration: dataModal.duration,
                    note: dataModal.note,
                    end_to: end_to,
                },
            }),
        );
    }

    public bgSeason(): string {
        if (this.router.url !== "/cookie") {
            const mounth = moment().format("MMMM").toString().toLowerCase();
            return `url("assets/images/backgrounds/${mounth}_hd.png")`;
        } else {
            return "unset";
        }
    }

    edit_profile(): void {
        this.user = this.storage.getItem("user");
        this.dialog
            .open(DialogProfileComponent, {
                hasBackdrop: true,
                data: {
                    modalData: this.user,
                },
            })
            .afterClosed()
            .pipe(
                filter((result) => !!result),
                mergeMap((result) => {
                    this.user.userdata = result;
                    return this.apiUserService.apiChangeProfile(this.user);
                }),
            )
            .subscribe({
                next: (user) => {
                    this.storage.setItem("user", user);
                    this.askUserDataConfirmation = false;
                    this.toast.success(
                        "Aggiornamento Profilo",
                        "Profilo modificato con successo",
                    );
                },
                error: (err) => {
                    this.toast.error(
                        "Aggiornamento Profilo",
                        "Modifica Profilo fallita",
                    );
                },
            });
    }

    logout(): void {
        const externalUrl = environment.return_url;
        this.authService.logout().subscribe(() => {
            window.open(externalUrl, "_self");
        });
    }

    privacy() {
        this.router.navigate(["privacy"]);
    }

    openBrowserInfo() {
        const snackConfig = new MatSnackBarConfig();
        snackConfig.panelClass = [];
        const snackBarRef = this.snackBar.open(
            `Browser Supportati:\n
        - Safari >= 11.1.2 (high Sierra)\n
        - Chrome ultima versione (>=80)\n
        - Firefox ultima versione (>=74)\n
    `,
            "Ho Capito",
            {
                duration: 60000,
                panelClass: ["multiline-snackbar"],
                verticalPosition: "top", // 'top' | 'bottom'
                // horizontalPosition: 'end', //'start' | 'center' | 'end' | 'left' | 'right'
            },
        );

        snackBarRef.afterDismissed().subscribe(() => {
            this.storage.setItem("browser-info", true);
        });
    }

    seeAllTicket() {
        this.router.navigate([
            "appointments",
            { viewAll: "yes", id_user: this.storage.getItem("user")?.id },
        ]);
    }

    getTickets() {
        const obj: TicketScheduleRequest = {
            id_user: this.storage.getItem("user")?.id,
            schedule_start: moment().startOf("day").toISOString(),
            schedule_end: moment().endOf("day").toISOString(),
            platform: environment.platform,
            id_statuses: this.statusesToSearch,
        };
        this.apiTickeService
            .getTicketScheduledListUsingPost(obj)
            .subscribe((data) => {
                this.dataSource = new MatTableDataSource<ITicketScheduled>(
                    data,
                );
            });
    }

    private getTicketsWebsocket() {
        merge(
            this.socketService.getMessage(WsEvents.ticket.updated),
            this.socketService.getMessage(WsEvents.ticket.create),
        )
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: () => {
                    this.getTickets();
                },
            });
    }
}
